import {
  demoDojos,
  demoInstructeurs,
  demoEtudiants,
  demoDocuments,
} from "./demoData.js";
import { demoClasses, demoEvents, demoPresences } from "./demoScheduleData.js";
import { demoCeintures, demoProgressions, demoExamens } from "./demoProgressData.js";
import {
  demoMartialTitles,
  demoTitleApplications,
  demoTitleAuthority,
  demoTitleRegister,
} from "./demoTitleRecognitionData.js";
import {
  demoAuthorityDocuments,
  demoEvidenceDossiers,
  demoValidationLog,
  demoDojoDocuments,
} from "./demoAuthorityDocumentsData.js";
import {
  demoDocumentImportQueue,
  demoDocumentImportWarnings,
  secureBackendBlueprint as demoSecureBackendBlueprint,
} from "./documentImportData.js";

const STORAGE_KEY = "karate-phase1-data-v9";

export { STORAGE_KEY as DATA_STORAGE_KEY };

function cloneInitial() {
  return {
    dojos: structuredClone(demoDojos),
    instructeurs: structuredClone(demoInstructeurs),
    etudiants: structuredClone(demoEtudiants),
    documents: structuredClone(demoDocuments),
    classes: structuredClone(demoClasses),
    events: structuredClone(demoEvents),
    presences: structuredClone(demoPresences),
    ceintures: structuredClone(demoCeintures),
    progressions: structuredClone(demoProgressions),
    examens: structuredClone(demoExamens),
    martialTitles: structuredClone(demoMartialTitles),
    titleApplications: structuredClone(demoTitleApplications),
    titleAuthority: structuredClone(demoTitleAuthority),
    titleRegister: structuredClone(demoTitleRegister),
    authorityDocuments: structuredClone(demoAuthorityDocuments),
    evidenceDossiers: structuredClone(demoEvidenceDossiers),
    validationLog: structuredClone(demoValidationLog),
    dojoDocuments: structuredClone(demoDojoDocuments),
    documentImportQueue: structuredClone(demoDocumentImportQueue),
    documentImportWarnings: structuredClone(demoDocumentImportWarnings),
    secureBackendBlueprint: structuredClone(demoSecureBackendBlueprint),
  };
}

function mergeStateDefaults(parsed) {
  return {
    ...cloneInitial(),
    ...parsed,
    classes: parsed.classes ?? demoClasses,
    events: parsed.events ?? demoEvents,
    presences: parsed.presences ?? demoPresences,
    ceintures: parsed.ceintures ?? demoCeintures,
    progressions: parsed.progressions ?? demoProgressions,
    examens: parsed.examens ?? demoExamens,
    martialTitles: parsed.martialTitles ?? demoMartialTitles,
    titleApplications: parsed.titleApplications ?? demoTitleApplications,
    titleAuthority: parsed.titleAuthority ?? demoTitleAuthority,
    titleRegister: parsed.titleRegister ?? demoTitleRegister,
    authorityDocuments: parsed.authorityDocuments ?? demoAuthorityDocuments,
    evidenceDossiers: parsed.evidenceDossiers ?? demoEvidenceDossiers,
    validationLog: parsed.validationLog ?? demoValidationLog,
    dojoDocuments: parsed.dojoDocuments ?? demoDojoDocuments,
    documentImportQueue: parsed.documentImportQueue ?? demoDocumentImportQueue,
    documentImportWarnings: parsed.documentImportWarnings ?? demoDocumentImportWarnings,
    secureBackendBlueprint: parsed.secureBackendBlueprint ?? demoSecureBackendBlueprint,
  };
}

function loadFromStorage() {
  if (typeof localStorage === "undefined") return cloneInitial();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const legacy =
        localStorage.getItem("karate-phase1-data-v7") ||
        localStorage.getItem("karate-phase1-data-v6") ||
        localStorage.getItem("karate-phase1-data-v5") ||
        localStorage.getItem("karate-phase1-data-v4") ||
        localStorage.getItem("karate-phase1-data-v3") ||
        localStorage.getItem("karate-phase1-data-v2") ||
        localStorage.getItem("karate-phase1-data-v1");
      if (legacy) {
        const merged = mergeStateDefaults(JSON.parse(legacy));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
        return merged;
      }
      return cloneInitial();
    }
    return mergeStateDefaults(JSON.parse(raw));
  } catch {
    return cloneInitial();
  }
}

let state = loadFromStorage();

function persist() {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("karate-data-updated"));
  }
}

export function getDataStore() {
  return state;
}

export function resetDataStore() {
  state = cloneInitial();
  persist();
  return state;
}

export function replaceCollection(key, rows) {
  state = { ...state, [key]: rows };
  syncSenseiDojoLinks();
  persist();
  return state;
}

export function mergeCollection(key, rows) {
  const existing = state[key] || [];
  const map = new Map(existing.map((r) => [r.id, r]));
  for (const row of rows) map.set(row.id, row);
  state = { ...state, [key]: [...map.values()] };
  syncSenseiDojoLinks();
  persist();
  return state;
}

/**
 * @param {'dojos'|'instructeurs'|'etudiants'|'documents'|'classes'|'events'|'presences'|'ceintures'|'progressions'|'examens'|'martialTitles'|'titleApplications'|'titleAuthority'|'titleRegister'|'authorityDocuments'|'evidenceDossiers'|'validationLog'|'dojoDocuments'|'documentImportQueue'|'documentImportWarnings'} key
 * @param {object} item
 */
export function addItem(key, item) {
  const list = state[key] || [];
  if (list.some((r) => r.id === item.id)) {
    throw new Error(`Identifiant déjà utilisé : ${item.id}`);
  }
  state = { ...state, [key]: [...list, item] };
  syncSenseiDojoLinks();
  persist();
  return state;
}

/**
 * @param {'dojos'|'instructeurs'|'etudiants'|'documents'|'classes'|'events'|'presences'|'ceintures'|'progressions'|'examens'|'martialTitles'|'titleApplications'|'titleAuthority'|'titleRegister'|'authorityDocuments'|'evidenceDossiers'|'validationLog'|'dojoDocuments'|'documentImportQueue'|'documentImportWarnings'} key
 * @param {string} id
 * @param {object} patch
 */
export function updateItem(key, id, patch) {
  const list = state[key] || [];
  const idx = list.findIndex((r) => r.id === id);
  if (idx < 0) throw new Error(`Entrée introuvable : ${id}`);
  const next = [...list];
  next[idx] = { ...next[idx], ...patch, id };
  state = { ...state, [key]: next };
  syncSenseiDojoLinks();
  persist();
  return state;
}

/** Désactivation visuelle (statut inactif). */
export function archiveItem(key, id) {
  return updateItem(key, id, { statut: "inactif" });
}

export function updateDocument(id, patch) {
  return updateItem("documents", id, patch);
}

/** @param {string} visibilite */
export function getDocumentVisibilityWarning(visibilite, titre = "") {
  if (visibilite === "public" && /protocole|black dragon|rituel|symbolisme/i.test(titre)) {
    return "Document sensible — ne pas publier en public sans validation officielle du Hanshi.";
  }
  if (visibilite === "public") return null;
  if (visibilite === "a_valider") return "Ce document reste non publié tant qu'il n'est pas validé.";
  return null;
}

/** Sync bidirectionnel dojo.sensei_id ↔ instructeur.dojo_id */
function syncSenseiDojoLinks() {
  const dojos = state.dojos || [];
  const instructeurs = state.instructeurs || [];
  let changed = false;

  const nextInstructeurs = instructeurs.map((inst) => {
    const dojo = dojos.find((d) => d.sensei_id === inst.id);
    const expectedDojoId = dojo?.id ?? inst.dojo_id;
    if (dojo && inst.dojo_id !== dojo.id) {
      changed = true;
      return { ...inst, dojo_id: dojo.id };
    }
    return inst;
  });

  const nextDojos = dojos.map((dojo) => {
    if (!dojo.sensei_id) return dojo;
    const inst = nextInstructeurs.find((i) => i.id === dojo.sensei_id);
    if (inst && inst.dojo_id && inst.dojo_id !== dojo.id) {
      changed = true;
      return { ...dojo, sensei_id: null };
    }
    return dojo;
  });

  if (changed) {
    state = { ...state, dojos: nextDojos, instructeurs: nextInstructeurs };
  }
}

export function getDojoById(id) {
  return state.dojos.find((d) => d.id === id) ?? null;
}

export function getInstructeurById(id) {
  return state.instructeurs.find((i) => i.id === id) ?? null;
}

export function countByDojo(dojoId) {
  return state.etudiants.filter((e) => e.dojo_id === dojoId && e.statut !== "inactif").length;
}

export function getImportContext() {
  return {
    dojoIds: state.dojos.map((d) => d.id),
    instructeurIds: state.instructeurs.map((i) => i.id),
  };
}

/** Statistiques tableau de bord Hanshi. */
export function getHanshiDashboardStats() {
  const dojos = state.dojos;
  const instructeurs = state.instructeurs;
  const etudiants = state.etudiants;
  const documents = state.documents;

  return {
    dojosTotal: dojos.length,
    dojosDemo: dojos.filter((d) => d.demo).length,
    dojosOfficial: dojos.filter((d) => !d.demo).length,
    dojosPending: dojos.filter((d) => d.statut === "a_confirmer").length,
    dojosWithoutSensei: dojos.filter((d) => !d.sensei_id && d.statut !== "inactif").length,
    instructeursTotal: instructeurs.length,
    instructeursUnassigned: instructeurs.filter((i) => !i.dojo_id && i.titre.toLowerCase().includes("sensei")).length,
    etudiantsTotal: etudiants.length,
    etudiantsDemo: etudiants.filter((e) => e.demo).length,
    documentsToValidate: documents.filter((d) => d.visibilite === "a_valider").length,
    documentsPublic: documents.filter((d) => d.visibilite === "public").length,
    pendingConfirmations:
      dojos.filter((d) => d.statut === "a_confirmer").length +
      instructeurs.filter((i) => i.statut === "a_confirmer").length +
      etudiants.filter((e) => e.statut === "a_confirmer").length,
    targetDojos: 10,
  };
}

/** Indicateur de préparation Phase 1 (données démo + attente client). */
export function getPhase1Preparation() {
  const stats = getHanshiDashboardStats();
  const dojoProgress = Math.min(stats.dojosOfficial / stats.targetDojos, 1);
  const leadershipOk = stats.instructeursTotal >= 8 ? 1 : stats.instructeursTotal / 8;
  const docsOk = stats.documentsToValidate === 0 ? 1 : 0.5;
  const readinessPercent = Math.round(((dojoProgress * 0.5 + leadershipOk * 0.3 + docsOk * 0.2) / 1) * 100);

  let statusLabel = "En attente du fichier client";
  if (stats.dojosOfficial >= stats.targetDojos) {
    statusLabel = "Dojos officiels complets — vérifier étudiants et documents";
  } else if (stats.dojosDemo > 0 && stats.dojosOfficial === 0) {
    statusLabel = "Mode démonstration — données [DÉMO] uniquement";
  }

  return {
    readinessPercent: Math.max(readinessPercent, 15),
    statusLabel,
    awaitingClientFile: stats.dojosOfficial < stats.targetDojos,
  };
}

/** Présence légère — upsert par class_id + student_id + date */
export function upsertPresence(record) {
  const list = state.presences || [];
  const idx = list.findIndex(
    (p) => p.class_id === record.class_id && p.student_id === record.student_id && p.date === record.date,
  );
  let next;
  if (idx >= 0) {
    next = [...list];
    next[idx] = { ...next[idx], ...record, id: next[idx].id };
  } else {
    next = [...list, { ...record, id: record.id || `att-${Date.now()}` }];
  }
  state = { ...state, presences: next };
  persist();
  return state;
}

export function getPresencesForClass(classId, date) {
  return (state.presences || []).filter((p) => p.class_id === classId && (!date || p.date === date));
}

export function getClassesByDojo(dojoId) {
  return (state.classes || []).filter((c) => c.dojo_id === dojoId && c.statut !== "inactif");
}

export function getEventsByDojo(dojoId) {
  return (state.events || []).filter((e) => e.dojo_id === dojoId && e.statut !== "inactif");
}

export function getCeintureById(id) {
  return (state.ceintures || []).find((b) => b.id === id) ?? null;
}

export function getProgressionByStudentId(studentId) {
  return (state.progressions || []).find((p) => p.student_id === studentId) ?? null;
}

export function updateProgression(studentId, patch) {
  const list = state.progressions || [];
  const idx = list.findIndex((p) => p.student_id === studentId);
  if (idx < 0) throw new Error(`Progression introuvable : ${studentId}`);
  const next = [...list];
  next[idx] = { ...next[idx], ...patch, student_id: studentId };
  state = { ...state, progressions: next };
  persist();
  return next[idx];
}

export function updateProgressCriterion(studentId, criterionKey, patch) {
  const prog = getProgressionByStudentId(studentId);
  if (!prog) throw new Error(`Progression introuvable : ${studentId}`);
  const criteres = {
    ...prog.criteres,
    [criterionKey]: {
      ...prog.criteres[criterionKey],
      ...patch,
      updatedAt: new Date().toISOString().slice(0, 10),
    },
  };
  return updateProgression(studentId, { criteres });
}

export function setExamRecommendation(studentId, recommended, note = "") {
  const patch = {
    recommandation_examen: recommended,
    statut: recommended ? "recommande_examen" : "en_progression",
  };
  if (note) patch.notes_sensei = note;
  return updateProgression(studentId, patch);
}

export function getExamenByEventId(eventId) {
  return (state.examens || []).find((e) => e.event_id === eventId) ?? null;
}

export function updateTitleApplication(id, patch) {
  return updateItem("titleApplications", id, patch);
}

export function addTitleApplication(item) {
  return addItem("titleApplications", item);
}

export function addAuthorityDocument(item) {
  return addItem("authorityDocuments", item);
}

export function getTitleById(id) {
  return (state.martialTitles || []).find((t) => t.id === id) ?? null;
}

export function getRegisterForPerson(personId) {
  return (state.titleRegister || []).filter((r) => r.personne_id === personId);
}

export function getDossierBySubjectId(sujetId) {
  return (state.evidenceDossiers || []).find((d) => d.sujet_id === sujetId) ?? null;
}

export function getDocumentsForApplication(appId) {
  return (state.authorityDocuments || []).filter((d) => d.demande_titre_id === appId);
}

export function getDocumentsForAuthority(authId) {
  return (state.authorityDocuments || []).filter((d) => d.autorite_id === authId);
}

export function updateAuthorityDocument(id, patch) {
  return updateItem("authorityDocuments", id, patch);
}

export function updateEvidenceDossier(id, patch) {
  return updateItem("evidenceDossiers", id, patch);
}

export function appendValidationLog(entry) {
  const list = state.validationLog || [];
  state = { ...state, validationLog: [...list, entry] };
  persist();
  return entry;
}

export function validateAuthorityDocument(id, action, acteur = "admin", role = "hanshi") {
  const doc = (state.authorityDocuments || []).find((d) => d.id === id);
  if (!doc) throw new Error(`Document introuvable : ${id}`);
  const statusMap = {
    accept: "accepte",
    refuse: "refuse",
    request: "a_fournir",
    replace: "a_remplacer",
    receive: "recu",
    review: "en_revision",
  };
  const newStatus = statusMap[action] || doc.statut_validation;
  updateAuthorityDocument(id, { statut_validation: newStatus });
  appendValidationLog({
    id: `log-${Date.now()}`,
    date: new Date().toISOString(),
    acteur,
    role_acteur: role,
    action: action === "accept" ? "document_accepte" : action === "refuse" ? "document_refuse" : "document_demande",
    cible_type: "authorityDocument",
    cible_id: id,
    ancien_statut: doc.statut_validation,
    nouveau_statut: newStatus,
    commentaire: `[DÉMO] Action ${action} sur document.`,
    document_id: id,
    demande_titre_id: doc.demande_titre_id,
    visibilite: "interne",
    demo: true,
  });
  return doc;
}

/* ── Import contrôlé de documents (BT-023) — métadonnées seulement ── */

/** Ajoute une entrée d'import (métadonnées) dans la file. */
export function addDocumentImport(entry) {
  const list = state.documentImportQueue || [];
  if (list.some((r) => r.id === entry.id)) {
    throw new Error(`Import déjà présent : ${entry.id}`);
  }
  state = { ...state, documentImportQueue: [...list, entry] };
  persist();
  return entry;
}

export function updateDocumentImport(id, patch) {
  return updateItem("documentImportQueue", id, patch);
}

export function addDocumentImportWarning(warning) {
  const list = state.documentImportWarnings || [];
  state = { ...state, documentImportWarnings: [...list, warning] };
  persist();
  return warning;
}

export function getWarningsForImport(importId) {
  return (state.documentImportWarnings || []).filter((w) => w.import_id === importId);
}

/**
 * Confirme un import local : crée une entrée document (authorityDocuments ou
 * dojoDocuments) au statut « reçu — validation requise » et journalise.
 * Aucun fichier réel n'est stocké — métadonnées seulement.
 * Ne marque JAMAIS un document officiel ou validé automatiquement.
 */
export function confirmDocumentImport(id, acteur = "admin", role = "hanshi") {
  const entry = (state.documentImportQueue || []).find((r) => r.id === id);
  if (!entry) throw new Error(`Import introuvable : ${id}`);

  const targetKey = entry.categorie_proposee === "dojo_general" ? "dojoDocuments" : "authorityDocuments";
  const docId = `doc-import-${id}`;

  const baseDoc = {
    id: docId,
    titre: entry.fichier_nom,
    type_document: entry.type_document_propose || "autre",
    categorie: entry.categorie_proposee || "autre",
    module_lie: entry.module_lie,
    personne_liee: entry.personne_liee || "—",
    statut_validation: "recu_validation_requise",
    niveau_sensibilite: entry.sensibilite_proposee || "confidentiel",
    date_ajout: new Date().toISOString().slice(0, 10),
    source: "import client",
    notes: entry.notes || "Import local — fichier non stocké officiellement.",
    demo: false,
    fichier_stocke: false,
  };

  const doc =
    targetKey === "authorityDocuments"
      ? {
          ...baseDoc,
          demande_titre_id: null,
          titre_id: null,
          autorite_id: null,
          reconnaissance_id: null,
          fichier_nom: entry.fichier_nom,
          fichier_type: entry.fichier_type,
          date_document: null,
          ajoute_par: acteur,
          statut_demo: "import",
        }
      : baseDoc;

  const list = state[targetKey] || [];
  state = {
    ...state,
    [targetKey]: [...list.filter((d) => d.id !== docId), doc],
  };

  updateDocumentImport(id, { statut_import: "confirme" });

  appendValidationLog({
    id: `log-import-${Date.now()}`,
    date: new Date().toISOString(),
    acteur,
    role_acteur: role,
    action: "import_confirme",
    cible_type: targetKey === "authorityDocuments" ? "authorityDocument" : "dojoDocument",
    cible_id: docId,
    ancien_statut: null,
    nouveau_statut: "recu_validation_requise",
    commentaire: "Import local confirmé — reçu, validation requise. Non officiel.",
    document_id: docId,
    demande_titre_id: null,
    visibilite: "interne",
    demo: false,
  });

  persist();
  return doc;
}

export function cancelDocumentImport(id, acteur = "admin", role = "hanshi") {
  const entry = (state.documentImportQueue || []).find((r) => r.id === id);
  if (!entry) throw new Error(`Import introuvable : ${id}`);
  updateDocumentImport(id, { statut_import: "annule" });
  appendValidationLog({
    id: `log-import-cancel-${Date.now()}`,
    date: new Date().toISOString(),
    acteur,
    role_acteur: role,
    action: "import_annule",
    cible_type: "documentImport",
    cible_id: id,
    ancien_statut: entry.statut_import,
    nouveau_statut: "annule",
    commentaire: "Import local annulé.",
    document_id: null,
    demande_titre_id: null,
    visibilite: "interne",
    demo: false,
  });
  return entry;
}

/** Vue dojos avec Sensei et effectifs pour Hanshi. */
export function getDojosOverview() {
  return state.dojos.map((d) => ({
    ...d,
    senseiNom: d.sensei_id ? getInstructeurById(d.sensei_id)?.nom ?? "—" : "—",
    etudiantsCount: countByDojo(d.id),
  }));
}
